import type {
  ActivationCodeResult,
  ActivityItem,
  AdminAccountSummary,
  AdminDeviceOverview,
  AdminPaginatedResult,
  AdminApi,
  AdminUser,
  AuthSession,
  CreateWarungInput,
  DashboardData,
  DeviceSummary,
  GoogleLoginInput,
  HealthStatus,
  LoginInput,
  ListAccountsQuery,
  ListActivitiesQuery,
  ListAdminDevicesQuery,
  ListWarungsQuery,
  ProgramSettings,
  ResetAccountAccessResult,
  SubscriptionSummary,
  SyncOverview,
  SyncState,
  UpdateSubscriptionInput,
  UpdateWarungInput,
  WarungDetail,
  WarungSummary,
  WebAccessInput,
  WebAccessResult,
} from '@/types/admin'
import { createUiId } from '@/utils/id'

interface MockDatabase {
  warungs: WarungDetail[]
  settings: ProgramSettings
  activities: ActivityItem[]
}

const STORAGE_KEY = 'warung-digital-admin-v1.2.0'
const now = Date.now()
const isoAgo = (minutes: number): string => new Date(now - minutes * 60_000).toISOString()

const devices: readonly DeviceSummary[] = [
  { id: 'DEV-001', name: 'Samsung A15', platform: 'android', appVersion: '1.2.0', status: 'active', lastSeenAt: isoAgo(8), pendingSync: 0, conflictCount: 0 },
]

const seed: MockDatabase = {
  warungs: [
    {
      id: '5df447c7-0010-4eca-b001-000000000001', code: 'WRG-001', name: 'Toko Berkah', ownerName: 'Rina', phone: '081234567801', area: 'Koto Baru', status: 'active',
      createdAt: isoAgo(20_000), lastSeenAt: isoAgo(8), pendingSync: 0, conflictCount: 0, deviceCount: 1, appVersion: '1.2.0', supportNote: '', activationExpiresAt: null, activatedAt: isoAgo(19_500),
      devices,
      onboarding: { profileCompleted: true, firstTransactionAt: isoAgo(8_200), pinConfigured: true },
    },
    {
      id: '5df447c7-0010-4eca-b001-000000000002', code: 'WRG-002', name: 'Warung Mak Etek', ownerName: 'Lina', phone: '081234567802', area: 'Selayo', status: 'active',
      createdAt: isoAgo(18_000), lastSeenAt: isoAgo(42), pendingSync: 3, conflictCount: 0, deviceCount: 1, appVersion: '1.2.0', supportNote: 'Perlu dibantu mengatur daftar barang awal.', activationExpiresAt: null, activatedAt: isoAgo(17_500),
      devices: [{ id: 'DEV-002', name: 'Oppo A57', platform: 'android', appVersion: '1.2.0', status: 'active', lastSeenAt: isoAgo(42), pendingSync: 3, conflictCount: 0 }],
      onboarding: { profileCompleted: true, firstTransactionAt: isoAgo(6_000), pinConfigured: true },
    },
    {
      id: '5df447c7-0010-4eca-b001-000000000003', code: 'WRG-003', name: 'Warung Pak Rudi', ownerName: 'Rudi', phone: '081234567803', area: 'Cupak', status: 'active',
      createdAt: isoAgo(15_000), lastSeenAt: isoAgo(230), pendingSync: 0, conflictCount: 0, deviceCount: 1, appVersion: '1.2.0', supportNote: '', activationExpiresAt: null, activatedAt: isoAgo(14_500),
      devices: [{ id: 'DEV-003', name: 'Redmi Note 12', platform: 'android', appVersion: '1.2.0', status: 'active', lastSeenAt: isoAgo(230), pendingSync: 0, conflictCount: 0 }],
      onboarding: { profileCompleted: true, firstTransactionAt: isoAgo(4_500), pinConfigured: true },
    },
    {
      id: '5df447c7-0010-4eca-b001-000000000004', code: 'WRG-004', name: 'Kedai Harian', ownerName: 'Doni', phone: '081234567804', area: 'Talang', status: 'attention',
      createdAt: isoAgo(12_000), lastSeenAt: isoAgo(4_500), pendingSync: 7, conflictCount: 1, deviceCount: 1, appVersion: '1.2.0', supportNote: 'Sinkronisasi menumpuk. Jadwalkan pendampingan langsung.', activationExpiresAt: null, activatedAt: isoAgo(11_500),
      devices: [{ id: 'DEV-004', name: 'Vivo Y22', platform: 'android', appVersion: '1.2.0', status: 'active', lastSeenAt: isoAgo(4_500), pendingSync: 7, conflictCount: 1 }],
      onboarding: { profileCompleted: true, firstTransactionAt: isoAgo(3_500), pinConfigured: true },
    },
    {
      id: '5df447c7-0010-4eca-b001-000000000005', code: 'WRG-005', name: 'Warung Sederhana', ownerName: 'Yanti', phone: '081234567805', area: 'Guguak', status: 'active',
      createdAt: isoAgo(10_000), lastSeenAt: isoAgo(95), pendingSync: 1, conflictCount: 0, deviceCount: 2, appVersion: '1.2.0', supportNote: 'Sedang mencoba perangkat kedua.', activationExpiresAt: null, activatedAt: isoAgo(9_500),
      devices: [
        { id: 'DEV-005', name: 'Realme C55', platform: 'android', appVersion: '1.2.0', status: 'active', lastSeenAt: isoAgo(95), pendingSync: 1, conflictCount: 0 },
        { id: 'DEV-006', name: 'Samsung A04', platform: 'android', appVersion: '1.2.0', status: 'active', lastSeenAt: isoAgo(180), pendingSync: 0, conflictCount: 0 },
      ],
      onboarding: { profileCompleted: true, firstTransactionAt: isoAgo(2_500), pinConfigured: true },
    },
  ],
  settings: {
    programName: 'Galeh Rancak', targetWarungs: 5, initialArea: 'Kabupaten Solok', supportWhatsApp: '085110555775',
    dataRetentionDays: 365, allowAggregateAnalytics: true, showTransactionDetailsToAdmin: false,
  },
  activities: [
    { id: 'ACT-001', kind: 'sync_retried', title: 'Sinkronisasi diperiksa', description: 'Antrean seluruh warung diperiksa dari panel.', actor: 'Administrator', createdAt: isoAgo(70) },
    { id: 'ACT-002', kind: 'warung_updated', title: 'Catatan dukungan diperbarui', description: 'Catatan untuk Kedai Harian diperbarui.', actor: 'Administrator', createdAt: isoAgo(1_200) },
    { id: 'ACT-003', kind: 'warung_created', title: 'Warung ditambahkan', description: 'Warung Sederhana ditambahkan ke sistem.', actor: 'Administrator', createdAt: isoAgo(10_000) },
  ],
}

function clone<T>(value: T): T {
  return structuredClone(value)
}

function loadDatabase(): MockDatabase {
  const raw = globalThis.localStorage?.getItem(STORAGE_KEY)
  if (raw === null || raw === undefined) return clone(seed)
  try {
    return JSON.parse(raw) as MockDatabase
  } catch {
    return clone(seed)
  }
}

function saveDatabase(database: MockDatabase): void {
  globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(database))
}

function delay(ms = 180): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms))
}

function activity(database: MockDatabase, item: Omit<ActivityItem, 'id' | 'createdAt' | 'actor'>): void {
  database.activities.unshift({ ...item, id: createUiId('ACT'), createdAt: new Date().toISOString(), actor: 'Administrator' })
}

function statusForSync(warung: WarungDetail): SyncState {
  if (warung.conflictCount > 0) return 'conflict'
  if (warung.lastSeenAt === null || Date.now() - new Date(warung.lastSeenAt).getTime() > 24 * 60 * 60 * 1000) return 'offline'
  if (warung.pendingSync > 0) return 'pending'
  return 'healthy'
}

function summary(warung: WarungDetail): WarungSummary {
  return {
    id: warung.id,
    code: warung.code,
    name: warung.name,
    ownerName: warung.ownerName,
    phone: warung.phone,
    area: warung.area,
    status: warung.status,
    createdAt: warung.createdAt,
    lastSeenAt: warung.lastSeenAt,
    pendingSync: warung.pendingSync,
    conflictCount: warung.conflictCount,
    deviceCount: warung.deviceCount,
    appVersion: warung.appVersion,
    supportNote: warung.supportNote,
  }
}

export class MockAdminApi implements AdminApi {
  public async login(_input: LoginInput): Promise<AuthSession> {
    await delay(120)
    return {
      token: 'mock-admin-session',
      expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
      user: { id: 'ADMIN-MOCK', email: 'admin@warung.local', displayName: 'Administrator', role: 'admin', avatarUrl: null, authProvider: 'local' },
    }
  }

  public async loginWithGoogle(_input: GoogleLoginInput): Promise<AuthSession> {
    await delay(120)
    return {
      token: 'mock-google-admin-session',
      expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
      user: { id: 'ADMIN-MOCK', email: 'admin@warung.local', displayName: 'Administrator', role: 'admin', avatarUrl: null, authProvider: 'google' },
    }
  }

  public async me(): Promise<AdminUser> {
    await delay(60)
    return { id: 'ADMIN-MOCK', email: 'admin@warung.local', displayName: 'Administrator', role: 'admin', avatarUrl: null, authProvider: 'local' }
  }

  public async logout(): Promise<void> {
    await delay(60)
  }

  public async health(): Promise<HealthStatus> {
    await delay(80)
    return { ok: true, service: 'mock-admin-api', version: '1.3.0', checkedAt: new Date().toISOString(), message: 'Mode demo lokal aktif.' }
  }

  public async getDashboard(): Promise<DashboardData> {
    await delay()
    const database = loadDatabase()
    const pendingSyncOperations = database.warungs.reduce((total, warung) => total + warung.pendingSync, 0)
    const conflictOperations = database.warungs.reduce((total, warung) => total + warung.conflictCount, 0)
    return {
      registeredWarungs: database.warungs.length,
      activeToday: database.warungs.filter((warung) => warung.lastSeenAt !== null && Date.now() - new Date(warung.lastSeenAt).getTime() <= 24 * 60 * 60 * 1000).length,
      pendingSyncOperations,
      conflictOperations,
      dataLossIncidents: 0,
      ownerAccounts: database.warungs.length,
      verifiedAccounts: Math.max(0, database.warungs.length - 1),
      disabledAccounts: 0,
      pendingDeletionAccounts: 0,
      activeDevices: database.warungs.reduce((total, warung) => total + warung.deviceCount, 0),
      staleDevices: 1,
      proWarungs: database.warungs.filter((warung) => warung.subscription?.plan.code === 'pro').length,
      freeWarungs: database.warungs.filter((warung) => warung.subscription?.plan.code !== 'pro').length,
      adoptionTrend: [
        { date: isoAgo(6 * 1_440), label: 'Sen', activeWarungs: 2, transactions: 21 },
        { date: isoAgo(5 * 1_440), label: 'Sel', activeWarungs: 3, transactions: 36 },
        { date: isoAgo(4 * 1_440), label: 'Rab', activeWarungs: 3, transactions: 44 },
        { date: isoAgo(3 * 1_440), label: 'Kam', activeWarungs: 4, transactions: 61 },
        { date: isoAgo(2 * 1_440), label: 'Jum', activeWarungs: 4, transactions: 58 },
        { date: isoAgo(1 * 1_440), label: 'Sab', activeWarungs: 5, transactions: 79 },
        { date: new Date().toISOString(), label: 'Hari ini', activeWarungs: 4, transactions: 67 },
      ],
      readinessTasks: [
        { id: 'TASK-1', title: 'Transaksi pertama', description: 'Pastikan semua warung dapat menyimpan transaksi tanpa bantuan.', completed: true },
        { id: 'TASK-2', title: 'Catat bon', description: 'Uji pembayaran sebagian dan sisa bon pada minimal tiga warung.', completed: false },
      ],
    }
  }

  public async listWarungs(query?: ListWarungsQuery): Promise<AdminPaginatedResult<WarungSummary>> {
    await delay()
    let values = loadDatabase().warungs
    const search = query?.search?.trim().toLocaleLowerCase('id-ID') ?? ''
    if (search.length > 0) {
      values = values.filter((warung) => `${warung.name} ${warung.ownerName} ${warung.area} ${warung.code}`.toLocaleLowerCase('id-ID').includes(search))
    }
    if (query?.status !== undefined && query.status !== 'all') values = values.filter((warung) => warung.status === query.status)
    const page = Math.max(1, query?.page ?? 1)
    const perPage = Math.min(100, Math.max(1, query?.perPage ?? 20))
    const total = values.length
    const start = (page - 1) * perPage
    return { items: values.slice(start, start + perPage).map(summary), meta: { page, perPage, total, totalPages: Math.max(1, Math.ceil(total / perPage)) } }
  }

  public async listAccounts(query?: ListAccountsQuery): Promise<AdminPaginatedResult<AdminAccountSummary>> {
    await delay()
    let values: AdminAccountSummary[] = loadDatabase().warungs.map((warung) => ({
      id: `account-${warung.id}`, warungId: warung.id, warungCode: warung.code, warungName: warung.name, displayName: warung.ownerName,
      username: warung.code, email: `${warung.code.toLocaleLowerCase('id-ID')}@contoh.id`, role: 'OWNER', active: warung.status !== 'inactive', mustChangePassword: false,
      emailVerifiedAt: warung.status === 'inactive' ? null : warung.createdAt, authProvider: 'password', lastLoginAt: warung.lastSeenAt, createdAt: warung.createdAt,
      activeSessions: warung.lastSeenAt === null ? 0 : 1, activeDevices: warung.deviceCount, lastDeviceSeenAt: warung.lastSeenAt, deletionScheduledFor: null, plan: warung.subscription?.plan.code ?? 'free',
    }))
    const search = query?.search?.trim().toLocaleLowerCase('id-ID') ?? ''
    if (search) values = values.filter((item) => `${item.displayName} ${item.email ?? ''} ${item.username} ${item.warungName}`.toLocaleLowerCase('id-ID').includes(search))
    if (query?.status === 'active') values = values.filter((item) => item.active)
    if (query?.status === 'disabled') values = values.filter((item) => !item.active)
    if (query?.status === 'unverified') values = values.filter((item) => item.email !== null && item.emailVerifiedAt === null)
    if (query?.status === 'deletion') values = values.filter((item) => item.deletionScheduledFor !== null)
    if (query?.auth === 'google') values = values.filter((item) => item.authProvider === 'google' || item.authProvider === 'both')
    if (query?.auth === 'password') values = values.filter((item) => item.authProvider === 'password' || item.authProvider === 'both')
    if (query?.plan !== undefined && query.plan !== 'all') values = values.filter((item) => item.plan === query.plan)
    const page = Math.max(1, query?.page ?? 1); const perPage = Math.min(100, Math.max(10, query?.perPage ?? 20)); const total = values.length; const start = (page - 1) * perPage
    return { items: clone(values.slice(start, start + perPage)), meta: { page, perPage, total, totalPages: Math.max(1, Math.ceil(total / perPage)) } }
  }

  public async setAccountActive(_id: string, active: boolean): Promise<{ readonly updated: boolean; readonly active: boolean }> { await delay(); return { updated: true, active } }
  public async revokeAccountSessions(_id: string): Promise<{ readonly revoked: boolean }> { await delay(); return { revoked: true } }
  public async resetAccountAccess(id: string): Promise<ResetAccountAccessResult> {
    await delay(); const account = (await this.listAccounts({ perPage: 100 })).items.find((item) => item.id === id); if (account === undefined) throw new Error('Akun tidak ditemukan.')
    return { username: account.username, email: account.email, temporaryPassword: 'GalehRancak42', mustChangePassword: true }
  }

  public async listDevices(query?: ListAdminDevicesQuery): Promise<AdminPaginatedResult<AdminDeviceOverview>> {
    await delay()
    let values: AdminDeviceOverview[] = loadDatabase().warungs.flatMap((warung) => warung.devices.map((device) => ({
      id: device.id, warungId: warung.id, warungCode: warung.code, warungName: warung.name, ownerName: warung.ownerName, label: null, name: device.name,
      platform: device.platform, appVersion: device.appVersion, status: device.status === 'revoked' ? 'revoked' : (device.lastSeenAt === null || Date.now() - new Date(device.lastSeenAt).getTime() > 7 * 86400000 ? 'stale' : 'active'),
      lastSeenAt: device.lastSeenAt, pendingSync: device.pendingSync, conflictCount: device.conflictCount, createdAt: warung.createdAt, revokedAt: device.status === 'revoked' ? device.lastSeenAt : null,
    })))
    const search = query?.search?.trim().toLocaleLowerCase('id-ID') ?? ''
    if (search) values = values.filter((item) => `${item.label ?? ''} ${item.name} ${item.warungName} ${item.warungCode} ${item.ownerName}`.toLocaleLowerCase('id-ID').includes(search))
    if (query?.status !== undefined && query.status !== 'all') values = values.filter((item) => item.status === query.status)
    const page = Math.max(1, query?.page ?? 1); const perPage = Math.min(100, Math.max(10, query?.perPage ?? 20)); const total = values.length; const start = (page - 1) * perPage
    return { items: clone(values.slice(start, start + perPage)), meta: { page, perPage, total, totalPages: Math.max(1, Math.ceil(total / perPage)) } }
  }
  public async revokeDevice(_id: string): Promise<{ readonly revoked: boolean }> { await delay(); return { revoked: true } }

  public async getWarung(id: string): Promise<WarungDetail> {
    await delay()
    const warung = loadDatabase().warungs.find((item) => item.id === id)
    if (warung === undefined) throw new Error('Data warung tidak ditemukan.')
    return clone(warung)
  }

  public async createWarung(input: CreateWarungInput): Promise<WarungDetail> {
    await delay(260)
    const database = loadDatabase()
    const sequence = database.warungs.length + 1
    const warung: WarungDetail = {
      id: createUiId('warung'), code: `WRG-${String(sequence).padStart(3, '0')}`, name: input.name.trim(), ownerName: input.ownerName.trim(),
      phone: input.phone.trim(), area: input.area.trim(), status: 'inactive', createdAt: new Date().toISOString(), lastSeenAt: null,
      pendingSync: 0, conflictCount: 0, deviceCount: 0, appVersion: null, supportNote: 'Menunggu instalasi aplikasi dan transaksi pertama.', activationCode: String(Math.floor(10000000 + Math.random() * 90000000)), activationExpiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), activatedAt: null, devices: [],
      onboarding: { profileCompleted: false, firstTransactionAt: null, pinConfigured: false },
    }
    database.warungs.push(warung)
    activity(database, { kind: 'warung_created', title: 'Warung ditambahkan', description: `${warung.name} ditambahkan ke sistem.` })
    saveDatabase(database)
    return clone(warung)
  }

  public async updateWarung(id: string, input: UpdateWarungInput): Promise<WarungDetail> {
    await delay(220)
    const database = loadDatabase()
    const index = database.warungs.findIndex((item) => item.id === id)
    if (index < 0) throw new Error('Data warung tidak ditemukan.')
    const existing = database.warungs[index]
    if (existing === undefined) throw new Error('Data warung tidak ditemukan.')
    const updated: WarungDetail = { ...existing, ...input }
    database.warungs[index] = updated
    activity(database, { kind: 'warung_updated', title: 'Data warung diperbarui', description: `${updated.name} diperbarui dari panel.` })
    saveDatabase(database)
    return clone(updated)
  }

  public async generateActivationCode(id: string): Promise<ActivationCodeResult> {
    await delay(160)
    const database = loadDatabase()
    const index = database.warungs.findIndex((item) => item.id === id)
    if (index < 0) throw new Error('Data warung tidak ditemukan.')
    const code = String(Math.floor(10000000 + Math.random() * 90000000))
    const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    const existing = database.warungs[index]
    if (existing === undefined) throw new Error('Data warung tidak ditemukan.')
    database.warungs[index] = { ...existing, activationCode: code, activationExpiresAt: expiresAt }
    activity(database, { kind: 'warung_updated', title: 'Kode aktivasi dibuat', description: `Kode aktivasi baru dibuat untuk ${existing.name}.` })
    saveDatabase(database)
    return { activationCode: code, activationExpiresAt: expiresAt }
  }

  public async generateWebAccess(id: string, input: WebAccessInput = {}): Promise<WebAccessResult> {
    const warung = loadDatabase().warungs.find((item) => item.id === id)
    if (warung === undefined) throw new Error('Warung tidak ditemukan.')
    const email = input.email?.trim().toLocaleLowerCase('id-ID') || null
    return { username: warung.code, email, temporaryPassword: 'Warung12345', mustChangePassword: true }
  }

  public async updateSubscription(id: string, input: UpdateSubscriptionInput): Promise<SubscriptionSummary> {
    await delay()
    const database = loadDatabase()
    const index = database.warungs.findIndex((item) => item.id === id)
    if (index < 0) throw new Error('Data warung tidak ditemukan.')
    const subscription: SubscriptionSummary = {
      id: `mock-subscription-${id}`,
      status: 'active',
      source: 'manual',
      plan: { code: input.plan, name: input.plan === 'pro' ? 'Pro' : 'Free', description: input.plan === 'pro' ? 'Paket Pro Galeh Rancak.' : 'Paket Free Galeh Rancak.' },
      startedAt: new Date().toISOString(), currentPeriodStart: new Date().toISOString(), currentPeriodEnd: input.periodEnd ?? null, graceUntil: null,
      entitlements: input.plan === 'pro'
        ? { web_access: true, cloud_sync: true, multi_device: true, cloud_backup: true, team_users: true, advanced_reports: true, advanced_insights: true, advanced_export: true, audit_log: true, inventory_control: true, supplier_management: true, customer_debt_management: true, finance_tracking: true, thermal_printing: true }
        : { web_access: false, cloud_sync: true, multi_device: true, cloud_backup: true, team_users: false, advanced_reports: false, advanced_insights: false, advanced_export: false, audit_log: false, inventory_control: true, supplier_management: true, customer_debt_management: true, finance_tracking: true, thermal_printing: true },
      limits: input.plan === 'pro'
        ? { max_users: 10, max_devices: 10, min_sync_interval_seconds: 60, sync_batch_limit: 500, sync_retention_days: 180 }
        : { max_users: 1, max_devices: 2, min_sync_interval_seconds: 900, sync_batch_limit: 100, sync_retention_days: 30 },
    }
    const existing = database.warungs[index]
    if (existing === undefined) throw new Error('Data warung tidak ditemukan.')
    database.warungs[index] = { ...existing, subscription }
    saveDatabase(database)
    return subscription
  }

  public async getSyncOverview(): Promise<SyncOverview> {
    await delay()
    const warungs = loadDatabase().warungs
    const states = warungs.map((warung) => ({
      warungId: warung.id, code: warung.code, name: warung.name, ownerName: warung.ownerName, area: warung.area, state: statusForSync(warung),
      pendingSync: warung.pendingSync, conflictCount: warung.conflictCount, lastSeenAt: warung.lastSeenAt, devices: warung.devices,
    }))
    return {
      healthyWarungs: states.filter((item) => item.state === 'healthy').length,
      pendingOperations: states.reduce((total, item) => total + item.pendingSync, 0),
      conflicts: states.reduce((total, item) => total + item.conflictCount, 0),
      offlineWarungs: states.filter((item) => item.state === 'offline').length,
      warungs: states,
    }
  }

  public async retrySync(warungId?: string): Promise<SyncOverview> {
    await delay(420)
    const database = loadDatabase()
    database.warungs = database.warungs.map((warung) => {
      if (warungId !== undefined && warung.id !== warungId) return warung
      if (warung.conflictCount > 0) return warung
      return {
        ...warung,
        pendingSync: 0,
        devices: warung.devices.map((device) => ({ ...device, pendingSync: 0 })),
      }
    })
    const target = warungId === undefined ? 'seluruh warung' : database.warungs.find((item) => item.id === warungId)?.name ?? 'warung'
    activity(database, { kind: 'sync_retried', title: 'Sinkronisasi dicoba ulang', description: `Percobaan ulang dijalankan untuk ${target}.` })
    saveDatabase(database)
    return this.getSyncOverview()
  }

  public async getSettings(): Promise<ProgramSettings> {
    await delay()
    return clone(loadDatabase().settings)
  }

  public async saveSettings(input: ProgramSettings): Promise<ProgramSettings> {
    await delay(220)
    const database = loadDatabase()
    database.settings = clone(input)
    activity(database, { kind: 'settings_updated', title: 'Pengaturan program diperbarui', description: 'Identitas dan kebijakan layanan telah disimpan.' })
    saveDatabase(database)
    return clone(database.settings)
  }

  public async listActivities(query?: ListActivitiesQuery): Promise<AdminPaginatedResult<ActivityItem>> {
    await delay()
    let values = loadDatabase().activities
    if (query?.kind !== undefined && query.kind !== 'all') values = values.filter((item) => item.kind === query.kind)
    const page = Math.max(1, query?.page ?? 1)
    const perPage = Math.min(100, Math.max(1, query?.perPage ?? 20))
    const total = values.length
    const start = (page - 1) * perPage
    return { items: clone(values.slice(start, start + perPage)), meta: { page, perPage, total, totalPages: Math.max(1, Math.ceil(total / perPage)) } }
  }
}
